import { requiredPorts } from "./requiredPorts";
import type { FlowDocument, FlowNode } from "./types";

/**
 * How the designer canvas presents a node: a human title per node type, a
 * readable label per outgoing port, a one-line summary of the node's own
 * config, and the size its card is drawn at. Presentation only — port
 * names, config shapes and validity all stay owned by the flow contract
 * (requiredPorts.ts mirrors Go's port table; validateFlow.ts the schema).
 */

const TYPE_TITLES: Record<string, string> = {
  start: "Call starts",
  tts: "Say text",
  play: "Play audio",
  gather: "Key press menu",
  amd_gate: "AMD detection",
  hangup: "Hang up",
  branch: "Branch",
  repeat: "Repeat",
  consent: "Consent",
  tag: "Tag",
  http: "HTTP request",
  transfer: "Transfer",
};

export function nodeTitle(type: string): string {
  return TYPE_TITLES[type] ?? type;
}

const PORT_LABELS: Record<string, string> = {
  next: "Next",
  error: "Error",
  human: "Live person",
  machine: "Machine",
  notsure: "Not sure",
  timeout: "No input",
  invalid: "Invalid key",
  default: "Default",
  repeat: "Repeat",
  exceeded: "Repeats used up",
  failure: "Failed",
};

export function portLabel(port: string): string {
  if (port.startsWith("key:")) return `Key ${port.slice(4)}`;
  if (port.startsWith("case:")) return `Case ${port.slice(5)}`;
  return PORT_LABELS[port] ?? port;
}

/**
 * Every outgoing port the card draws a handle for: the type's required
 * ports first, in their own order, then any port an existing edge uses
 * that isn't one of them (a mistyped port must still have a handle to
 * attach to, or its edge would silently vanish from the canvas).
 */
export function nodePorts(document: FlowDocument, node: FlowNode): string[] {
  const ports = requiredPorts(node);
  for (const edge of document.edges) {
    if (edge.source === node.id && !ports.includes(edge.port)) ports.push(edge.port);
  }
  return ports;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/** A short, plain description of what this node will do on a call. */
export function nodeSummary(node: FlowNode): string {
  const config = record(node.config);
  switch (node.type) {
    case "start":
      return "The call is answered";
    case "tts":
      return text(config.text) ? `“${text(config.text)}”` : "No text yet";
    case "play":
      return text(config.asset_id) ? `Audio ${text(config.asset_id)}` : "No audio chosen";
    case "gather": {
      const keys = Array.isArray(config.valid_keys) ? config.valid_keys.filter((k) => typeof k === "string") : [];
      const prompt = record(config.prompt);
      const said = text(prompt.text);
      const keyList = keys.length ? `Keys ${keys.join(", ")}` : "No keys";
      return said ? `“${said}” · ${keyList}` : keyList;
    }
    case "amd_gate":
      return "Detects a live person or an answering machine";
    case "hangup":
      return text(config.reason) ? `Reason: ${text(config.reason)}` : "Ends the call";
    case "branch": {
      const cases = Array.isArray(config.cases) ? config.cases.length : 0;
      return `${cases} case${cases === 1 ? "" : "s"}`;
    }
    case "repeat":
      return typeof config.max_repeats === "number" ? `Up to ${config.max_repeats} repeats` : "Repeats a step";
    case "tag":
      return Array.isArray(config.tags) && config.tags.length ? `Tags ${config.tags.join(", ")}` : "Records a tag";
    case "consent":
      return text(config.action) ? `Consent: ${text(config.action)}` : "Records consent";
    case "http":
      return text(config.endpoint_id) ? `Endpoint ${text(config.endpoint_id)}` : "Calls an endpoint";
    case "transfer":
      return "Transfers the call";
    default:
      return "";
  }
}

export const NODE_WIDTH = 240;
const HEADER_HEIGHT = 36;
const SUMMARY_HEIGHT = 44;
const PORT_ROW_HEIGHT = 24;
const PADDING = 10;

/**
 * The card's drawn size. Fixed rather than measured: MiniMap and the
 * layout both need it before React Flow has measured anything (see
 * FlowCanvas.tsx), and a known height per port row is also what lets each
 * port's handle sit exactly beside its own label.
 */
export function nodeSize(document: FlowDocument, node: FlowNode): { width: number; height: number } {
  const ports = nodePorts(document, node).length;
  return { width: NODE_WIDTH, height: HEADER_HEIGHT + SUMMARY_HEIGHT + ports * PORT_ROW_HEIGHT + PADDING };
}

/** Vertical centre of port row `index`, measured from the card's top edge. */
export function portOffset(index: number): number {
  return HEADER_HEIGHT + SUMMARY_HEIGHT + index * PORT_ROW_HEIGHT + PORT_ROW_HEIGHT / 2;
}
